import React from "react";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHeading } from "@/components/primitives";
import { Icon } from "@/lib/icons";
import { useSeo } from "@/lib/useSeo";
import { IMAGES } from "@/lib/content";

const CATEGORIES = [
  {
    icon: "LayoutGrid",
    title: "Frontend",
    items: ["React", "Next.js", "TypeScript", "Modern responsive UI systems", "Progressive web technologies"],
  },
  {
    icon: "Server",
    title: "Backend",
    items: ["Python", "Node.js", "REST APIs", "WebSocket services", "Microservices", "Worker-based processing systems"],
  },
  {
    icon: "Database",
    title: "Data & Infrastructure",
    items: ["MongoDB", "Redis", "Cloud deployment", "Real-time data streams", "Queue-based processing", "Automated monitoring", "Scalable databases"],
  },
  {
    icon: "Activity",
    title: "Analytics & Automation",
    items: ["Risk analytics", "Trading analytics", "Automated account evaluation", "Real-time alerts", "Administrative reporting", "Behavioural monitoring", "Fraud detection support"],
  },
];

export default function Technologies() {
  useSeo({
    title: "Technologies",
    description:
      "The technology stack behind Vardhix Technologies — React, Next.js, Python, Node.js, MongoDB, Redis, cloud infrastructure, real-time analytics and automation.",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Technologies", path: "/technologies" }],
  });

  return (
    <>
      <PageHero
        overline="Technologies"
        title="A modern, scalable technology stack"
        sub="We build with proven, high-performance technologies chosen for reliability, real-time processing and scale. Only technologies actually used or planned are shown here."
      />

      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid md:grid-cols-2 gap-6">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 0.08}>
              <div className="glass rounded-3xl p-8 h-full hover:border-teal/40 transition-colors" data-testid={`tech-cat-${i}`}>
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                    <Icon name={c.icon} className="h-6 w-6" />
                  </span>
                  <h2 className="text-2xl font-heading font-semibold text-white">{c.title}</h2>
                </div>
                <div className="mt-7 flex flex-wrap gap-2.5">
                  {c.items.map((it) => (
                    <span key={it} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200 hover:border-teal/40 hover:text-teal transition-colors">
                      {it}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative py-20 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.servers} alt="" className="h-full w-full object-cover opacity-[0.14]" />
          <div className="absolute inset-0 bg-navy/85" />
        </div>
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <SectionHeading center overline="Engineering Principles" title="Reliability, performance and security by design" sub="Our systems are engineered to process large volumes of activity in real time, enforce rules automatically, and remain observable and resilient at scale." />
        </div>
      </section>
    </>
  );
}
